import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { calculateTimeDifference, formatTime } from './countdownUtils';

/**
 * Generic Countdown component
 * @param {Object} props
 * @param {Date} props.targetDate - The target date to count down to
 * @param {string} props.deadlineUpcomingMessage - The message to display when deadline is upcoming (e.g., "until EU Pay Transparency Directive goes live")
 * @param {string} props.deadlinePassedMessage - The message to display when deadline has passed (e.g., "EU Pay Transparency Directive is live as of")
 * @param {string} [props.callToActionUrl] - Optional URL for call-to-action link
 * @param {string} [props.locale] - The locale for date formatting (defaults to user's locale, falls back to "en-GB")
 */
const Countdown = ({ targetDate, deadlineUpcomingMessage, deadlinePassedMessage, callToActionUrl, locale }) => {
  const [timeData, setTimeData] = useState(null);
  const [isPastDeadline, setIsPastDeadline] = useState(false);

  // Get user's locale, fallback to en-GB - memoized once per page load
  const effectiveLocale = useMemo(() => {
    if (locale) {
      return locale;
    }
    // Try to get user's locale from navigator
    if (typeof navigator !== 'undefined') {
      return navigator.language || navigator.languages?.[0] || 'en-GB';
    }
    return 'en-GB';
  }, [locale]);

  // Memoize formatted target date string - calculated once per page load
  const formattedTargetDate = useMemo(() => {
    return targetDate.toLocaleDateString(effectiveLocale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }, [targetDate, effectiveLocale]);

  // Memoize the formatted time string based on timeData
  const timeString = useMemo(() => {
    return formatTime(timeData, isPastDeadline);
  }, [timeData, isPastDeadline]);

  // Memoize the call-to-action button component
  const callToActionButton = useMemo(() => {
    if (!callToActionUrl) return null;

    return (
      <a
        href={callToActionUrl}
        className="whitespace-nowrap rounded-full text-center outline-none ring-0 transition-all focus:border-none focus:outline-none font-medium inline-block px-5 py-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 active:scale-95 duration-200 bg-indigo-600 text-white mt-6"
        aria-label="Prepare with Sophare"
      >
        Prepare with Sophare
      </a>
    );
  }, [callToActionUrl]);

  // Calculate time remaining or time since deadline once on page load
  useEffect(() => {
    const result = calculateTimeDifference(targetDate);
    setTimeData({ years: result.years, months: result.months, days: result.days });
    setIsPastDeadline(result.isPastDeadline);
  }, [targetDate]);

  if (!timeData) {
    return null;
  }

  return (
    <div className="w-full bg-gray-50 py-12 sm:py-16 border-b">
      <div className="content max-w-[1120px] mx-auto px-4">
        <div className="text-center">
          <p className="text-2xl sm:text-3xl md:text-4xl font-semibold text-gray-900">
            {isPastDeadline ? (
              <>
                {`${deadlinePassedMessage} `}
                <span className="text-indigo-600">{timeString}</span>
              </>
            ) : (
              <>
                <span className="text-indigo-600">{timeString}</span>
                {` ${deadlineUpcomingMessage}`}
              </>
            )}
          </p>
          <p className="text-sm sm:text-base text-gray-600 mt-2">{formattedTargetDate}</p>
          {callToActionButton}
        </div>
      </div>
    </div>
  );
};

Countdown.propTypes = {
  targetDate: PropTypes.instanceOf(Date).isRequired,
  deadlineUpcomingMessage: PropTypes.string.isRequired,
  deadlinePassedMessage: PropTypes.string.isRequired,
  callToActionUrl: PropTypes.string,
  locale: PropTypes.string,
};

export default Countdown;
